/** Admin: Academy completion funnel, per track and per lesson (clients only, staff excluded). */
import { Fragment, useEffect, useState } from 'react';
import type { AcademyStats } from '@platform/shared';
import { cx, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';

const pct = (n: number, of: number) => (of > 0 ? `${Math.round((n / of) * 100)}%` : '—');

export function AcademyAdminTab() {
  const [data, setData] = useState<AcademyStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    api
      .get<AcademyStats>('/api/admin/academy')
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load Academy stats'));
  }, []);
  if (error) return <p className="text-down">{error}</p>;
  if (!data)
    return (
      <div className="grid min-h-[200px] place-items-center text-muted">
        <Spinner />
      </div>
    );
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-6">
        {[
          [data.learners, 'clients have completed a lesson'],
          [data.lessonsCompleted, 'lessons completed'],
        ].map(([v, l]) => (
          <span key={l as string} className="flex items-baseline gap-2">
            <span className="num text-[22px] font-semibold">{(v as number).toLocaleString('en-US')}</span>
            <span className="text-[13px] text-muted">{l}</span>
          </span>
        ))}
      </div>
      <p className="m-0 max-w-[720px] text-[13px] text-muted">
        A lesson counts once every quiz answer is right. A track is finished when all its lessons are. Clients see Crypto Pro’s track only when Crypto Pro is shown to them. Each finished track sends an{' '}
        <code className="num">academy_track_completed</code> webhook to the CRM.
      </p>
      <div className="overflow-x-auto rounded-xl border border-line">
        <table className="w-full min-w-[640px] border-collapse text-[13px]">
          <thead>
            <tr className="bg-panel text-left text-[11px] tracking-wide text-muted uppercase">
              <th className="px-3 py-2 font-medium">Track</th>
              <th className="px-3 py-2 text-right font-medium">Started</th>
              <th className="px-3 py-2 text-right font-medium">Finished</th>
              <th className="px-3 py-2 text-right font-medium">Finish rate</th>
              <th className="w-[30%] px-3 py-2 font-medium">Funnel</th>
            </tr>
          </thead>
          <tbody>
            {data.tracks.map((t) => (
              <Fragment key={t.id}>
                <tr className="cursor-pointer border-t border-line hover:bg-panel" onClick={() => setOpen(open === t.id ? null : t.id)}>
                  <td className="px-3 py-2.5 font-medium">
                    <button type="button" aria-expanded={open === t.id} className="text-left text-fg">
                      {open === t.id ? '▾' : '▸'} {t.title}
                    </button>
                  </td>
                  <td className="num px-3 py-2.5 text-right">{t.started}</td>
                  <td className="num px-3 py-2.5 text-right">{t.completed}</td>
                  <td className="num px-3 py-2.5 text-right">{pct(t.completed, t.started)}</td>
                  <td className="px-3 py-2.5">
                    <div className="h-2 overflow-hidden rounded-full bg-track">
                      <div className="h-full rounded-full bg-accent" style={{ width: t.started ? `${(t.completed / t.started) * 100}%` : 0 }} />
                    </div>
                  </td>
                </tr>
                {open === t.id &&
                  t.lessons.map((l, i) => (
                    <tr key={`${t.id}-${l.id}`} className="border-t border-line bg-panel/50 text-muted">
                      <td className="px-3 py-2 pl-8">
                        {i + 1}. {l.title}
                      </td>
                      <td />
                      <td className={cx('num px-3 py-2 text-right', l.completed === 0 && 'text-warn')}>{l.completed}</td>
                      <td className="num px-3 py-2 text-right">{pct(l.completed, t.started)}</td>
                      <td />
                    </tr>
                  ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
